/**
 * Authenticated app shell — top app bar with the OttoPilot wordmark and
 * the Logout button, side navigation with the Sites link, and a content
 * area that hosts the routed `<Outlet>`.
 *
 * Responsive:
 *   - On desktop (`md+`) the sidebar is a permanent `<Drawer>`.
 *   - On smaller breakpoints the sidebar collapses to a temporary
 *     drawer toggled by the menu icon in the top bar (mobile pattern
 *     per `standards/react-patterns.md`).
 */

import { useState } from "react";
import type { ReactElement } from "react";
import {
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import BusinessIcon from "@mui/icons-material/Business";
import InboxIcon from "@mui/icons-material/Inbox";
import InsightsIcon from "@mui/icons-material/Insights";
import LanguageIcon from "@mui/icons-material/Language";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import ScienceIcon from "@mui/icons-material/Science";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useLogout, useMe } from "../hooks/useAuth";
import { useMyOrgs } from "../hooks/useOrgs";
import { OrgSwitcher } from "./OrgSwitcher";
import { useToast } from "./ToastProvider";

const SIDEBAR_WIDTH = 240;

function SidebarNav({ onNavigate }: { onNavigate?: () => void }): ReactElement {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const isSites = path === "/sites" || path.startsWith("/sites/");
  const isAiStudio = path.startsWith("/ai/studio");
  const isAiUsage = path.startsWith("/ai/usage");
  const isLeads = path === "/leads" || path.startsWith("/leads/");
  const isAnalytics = path === "/analytics" || path.startsWith("/analytics/");
  const isExperiments = path === "/experiments" || path.startsWith("/experiments/");
  const isEnvDiagnostics = path === "/admin/env" || path.startsWith("/admin/env/");
  const isOrganizations = path.startsWith("/admin/organizations");

  // Issue #275: the Organizations sidebar entry deep-links to the user's
  // CURRENT org's detail page so a click lands on something concrete
  // rather than a list view (the list is the OrgSwitcher in the top bar).
  // We read the current org from the `me/orgs` cache populated by the
  // switcher; when it isn't ready yet we omit the link (rather than
  // render a broken / undefined href).
  const orgs = useMyOrgs();
  const currentOrgId = orgs.data?.currentOrganizationId ?? orgs.data?.orgs[0]?.id ?? undefined;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Toolbar>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          OttoPilot
        </Typography>
      </Toolbar>
      <Divider />
      <List sx={{ flexGrow: 1 }}>
        <ListItem disablePadding>
          <ListItemButton
            component={Link}
            to="/sites"
            selected={isSites}
            onClick={onNavigate}
            data-testid="sidebar-nav-sites-link"
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <LanguageIcon />
            </ListItemIcon>
            <ListItemText primary="Sites" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            component={Link}
            to="/ai/studio"
            selected={isAiStudio}
            onClick={onNavigate}
            data-testid="sidebar-nav-ai-studio-link"
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <AutoFixHighIcon />
            </ListItemIcon>
            <ListItemText primary="AI Studio" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            component={Link}
            to="/ai/usage"
            selected={isAiUsage}
            onClick={onNavigate}
            data-testid="sidebar-nav-ai-usage-link"
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <InsightsIcon />
            </ListItemIcon>
            <ListItemText primary="AI Usage" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            component={Link}
            to="/leads"
            selected={isLeads}
            onClick={onNavigate}
            data-testid="sidebar-nav-leads-link"
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <InboxIcon />
            </ListItemIcon>
            <ListItemText primary="Leads" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            component={Link}
            to="/analytics"
            selected={isAnalytics}
            onClick={onNavigate}
            data-testid="sidebar-nav-analytics-link"
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <AssessmentIcon />
            </ListItemIcon>
            <ListItemText primary="Analytics" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            component={Link}
            to="/experiments"
            selected={isExperiments}
            onClick={onNavigate}
            data-testid="sidebar-nav-experiments-link"
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <ScienceIcon />
            </ListItemIcon>
            <ListItemText primary="Experiments" />
          </ListItemButton>
        </ListItem>
        {currentOrgId ? (
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to={`/admin/organizations/${currentOrgId}`}
              selected={isOrganizations}
              onClick={onNavigate}
              data-testid="sidebar-nav-organizations-link"
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <BusinessIcon />
              </ListItemIcon>
              <ListItemText primary="Organization" />
            </ListItemButton>
          </ListItem>
        ) : null}
        <ListItem disablePadding>
          <ListItemButton
            component={Link}
            to="/admin/env"
            selected={isEnvDiagnostics}
            onClick={onNavigate}
            data-testid="sidebar-nav-env-link"
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <SettingsSuggestIcon />
            </ListItemIcon>
            <ListItemText primary="Env diagnostics" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );
}

export function AppLayout(): ReactElement {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileOpen, setMobileOpen] = useState(false);

  const navigate = useNavigate();
  const me = useMe();
  const logout = useLogout();
  const toast = useToast();

  const handleLogout = () => {
    logout.mutate(undefined, {
      onSuccess: () => {
        toast.showSuccess("Signed out.");
        void navigate({ to: "/login" });
      },
      onError: (err) => {
        toast.showError(err.message || "Could not sign out.");
      },
    });
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <AppBar
        position="fixed"
        color="default"
        elevation={0}
        data-testid="app-bar"
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          borderBottom: 1,
          borderColor: "divider",
          backgroundColor: "background.paper",
        }}
      >
        <Toolbar>
          {!isDesktop && (
            <IconButton
              edge="start"
              aria-label="Open navigation"
              onClick={() => setMobileOpen((v) => !v)}
              sx={{ mr: 1 }}
              data-testid="app-bar-menu-button"
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h4" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            OttoPilot
          </Typography>
          {/*
            Issue #275: the OrgSwitcher renders itself null when the user
            has zero orgs OR the `me/orgs` fetch is still pending — so we
            can mount it unconditionally without the top bar flashing
            empty placeholders.
          */}
          <OrgSwitcher />
          {me.data?.user.email ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mr: 2, display: { xs: "none", sm: "block" } }}
              data-testid="app-bar-user-email"
            >
              {me.data.user.email}
            </Typography>
          ) : null}
          <Button
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            disabled={logout.isPending}
            color="inherit"
            data-testid="app-bar-logout-button"
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: SIDEBAR_WIDTH }, flexShrink: { md: 0 } }}
        aria-label="Primary navigation"
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": { width: SIDEBAR_WIDTH },
          }}
        >
          <SidebarNav onNavigate={() => setMobileOpen(false)} />
        </Drawer>
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              width: SIDEBAR_WIDTH,
              boxSizing: "border-box",
              borderRight: 1,
              borderColor: "divider",
            },
          }}
        >
          <SidebarNav />
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Toolbar />
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, flexGrow: 1 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
